import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { PersonalInfoSection } from './PersonalInfoSection';
import { AddressSection } from './AddressSection';
import { BankingSection } from './BankingSection';
import { InsuranceSection } from './InsuranceSection';
import { WorkPermitSection } from './WorkPermitSection';
import { SwissPayrollSection } from './SwissPayrollSection';
import { supabase } from '@/integrations/supabase/client';
import type { Canton, InsuranceWithRate } from '@/types/insurance';
import { DEFAULT_VACATION_PERCENTAGE_4W } from '@/config/constants';

interface EmployeeFormData {
  // Personal Info
  firstname: string;
  lastname: string;
  email: string;
  phone: string;
  birth_date: string;
  gender: string;
  marital_status: string;
  nationality: string;
  has_children: boolean;
  kinderzulagen_eligible: boolean;

  // Address
  street_address: string;
  postal_code: string;
  city: string;
  country: string;

  // Banking
  iban: string;
  bank_name: string;
  bank_clearing_number: string;

  // Insurance & Tax
  ahv_number: string;
  health_insurance_provider: string;
  accident_insurance_number: string;
  pension_fund_number: string;
  tax_municipality: string;
  tax_canton: string;

  // Work
  role: string;
  department: string;
  position: string;
  responsibilities: string;
  employment_start_date: string;
  employment_end_date?: string;
  contract_type: string;
  work_type: string;
  working_hours_per_week: number;
  salary_type: string;
  hourly_rate?: number;
  monthly_salary?: number;
  vacation_days_per_year: number;

  // Work Permit (if applicable)
  work_permit_type?: string;
  work_permit_expires?: string;

  // Swiss Payroll
  payment_model: 'monthly' | 'hourly';
  selected_canton: Canton;
  vacation_model: 'fixed_days' | 'percentage_included';
  annual_vacation_days: number;
  vacation_percentage: number;
}

interface EmployeeFormProps {
  employeeId?: string;
  onSuccess?: () => void;
  onCancel?: () => void;
}

export const EmployeeForm: React.FC<EmployeeFormProps> = ({
  employeeId,
  onSuccess,
  onCancel
}) => {
  const [loading, setLoading] = useState(false);
  const [selectedInsurances, setSelectedInsurances] = useState<InsuranceWithRate[]>([]);
  const { toast } = useToast();

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors }
  } = useForm<EmployeeFormData>({
    defaultValues: {
      country: 'Switzerland',
      nationality: 'Swiss',
      contract_type: 'permanent',
      work_type: 'hourly',
      salary_type: 'monthly',
      working_hours_per_week: 42,
      vacation_days_per_year: 25,
      payment_model: 'monthly',
      selected_canton: 'ZH',
      vacation_model: 'fixed_days',
      annual_vacation_days: 20,
      vacation_percentage: DEFAULT_VACATION_PERCENTAGE_4W,
      role: 'employee'
    }
  });

  const watchedValues = watch();
  const employeeAge = watchedValues.birth_date ? 
    new Date().getFullYear() - new Date(watchedValues.birth_date).getFullYear() : undefined;

  // Load existing employee data
  useEffect(() => {
    if (employeeId) {
      loadEmployeeData();
    }
  }, [employeeId]);

  const loadEmployeeData = async () => {
    if (!employeeId) return;

    try {
      setLoading(true);
      
      // Load employee basic data
      const { data: employee, error: employeeError } = await supabase
        .from('employees')
        .select('*')
        .eq('id', employeeId)
        .single();

      if (employeeError) throw employeeError;

      // Load vacation settings
      const { data: vacationSettings } = await supabase
        .from('employee_vacation_settings')
        .select('*')
        .eq('employee_id', employeeId)
        .single();

      // Load insurance settings
      const { data: insuranceSettings } = await supabase
        .from('employee_insurance_settings')
        .select(`
          *,
          swiss_insurance_types (*)
        `)
        .eq('employee_id', employeeId);

      // Map the data to form format
      if (employee) {
        const formData: Partial<EmployeeFormData> = {
          ...employee,
          payment_model: employee.work_type === 'hourly' ? 'hourly' : 'monthly',
          selected_canton: employee.tax_canton as Canton || 'ZH',
          vacation_model: (vacationSettings?.vacation_model as 'fixed_days' | 'percentage_included') || 'fixed_days',
          annual_vacation_days: vacationSettings?.annual_days || 20,
          vacation_percentage: vacationSettings?.percentage_rate || DEFAULT_VACATION_PERCENTAGE_4W
        };

        // Reset form with loaded data
        Object.entries(formData).forEach(([key, value]) => {
          setValue(key as keyof EmployeeFormData, value);
        });

        // Set insurance settings
        if (insuranceSettings) {
          const mappedInsurances: InsuranceWithRate[] = insuranceSettings.map(setting => ({
            ...setting.swiss_insurance_types,
            rate_percent: setting.custom_rate_percent || 0,
            is_enabled: setting.is_enabled,
            is_custom_rate: !!setting.custom_rate_percent
          } as InsuranceWithRate));
          setSelectedInsurances(mappedInsurances);
        }
      }
    } catch (error) {
      console.error('Error loading employee:', error);
      toast({
        title: 'Fehler',
        description: 'Fehler beim Laden der Mitarbeiterdaten',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data: EmployeeFormData) => {
    try {
      setLoading(true);

      // Prepare employee data
      const employeeData = {
        firstname: data.firstname,
        lastname: data.lastname,
        name: `${data.firstname} ${data.lastname}`,
        email: data.email,
        phone: data.phone,
        birth_date: data.birth_date,
        gender: data.gender,
        marital_status: data.marital_status,
        nationality: data.nationality,
        has_children: data.has_children,
        kinderzulagen_eligible: data.kinderzulagen_eligible,
        street_address: data.street_address,
        postal_code: data.postal_code,
        city: data.city,
        country: data.country,
        address: `${data.street_address}, ${data.postal_code} ${data.city}`,
        iban: data.iban,
        bank_name: data.bank_name,
        bank_clearing_number: data.bank_clearing_number,
        ahv_number: data.ahv_number,
        health_insurance_provider: data.health_insurance_provider,
        accident_insurance_number: data.accident_insurance_number,
        pension_fund_number: data.pension_fund_number,
        tax_municipality: data.tax_municipality,
        tax_canton: data.selected_canton,
        role: data.role,
        department: data.department,
        position: data.position,
        responsibilities: data.responsibilities,
        employment_start_date: data.employment_start_date,
        employment_end_date: data.employment_end_date,
        contract_type: data.contract_type,
        work_type: data.payment_model === 'hourly' ? 'hourly' : 'monthly',
        working_hours_per_week: data.working_hours_per_week,
        salary_type: data.salary_type,
        hourly_rate: data.hourly_rate,
        monthly_salary: data.monthly_salary,
        vacation_days_per_year: data.annual_vacation_days,
        work_permit_type: data.work_permit_type,
        work_permit_expires: data.work_permit_expires
      };

      let employeeResult;

      if (employeeId) {
        // Update existing employee
        const { data: updatedEmployee, error } = await supabase
          .from('employees')
          .update(employeeData)
          .eq('id', employeeId)
          .select()
          .single();

        if (error) throw error;
        employeeResult = updatedEmployee;
      } else {
        // Create new employee
        const { data: newEmployee, error } = await supabase
          .from('employees')
          .insert(employeeData)
          .select()
          .single();

        if (error) throw error;
        employeeResult = newEmployee;
      }

      // Save vacation settings
      const vacationData = {
        employee_id: employeeResult.id,
        vacation_model: data.vacation_model,
        annual_days: data.vacation_model === 'fixed_days' ? data.annual_vacation_days : null,
        percentage_rate: data.vacation_model === 'percentage_included' ? data.vacation_percentage : null
      };

      await supabase
        .from('employee_vacation_settings')
        .upsert(vacationData, { onConflict: 'employee_id' });

      // Save insurance settings
      if (selectedInsurances.length > 0) {
        // Delete existing insurance settings
        await supabase
          .from('employee_insurance_settings')
          .delete()
          .eq('employee_id', employeeResult.id);

        // Insert new insurance settings
        const insuranceData = selectedInsurances.map(insurance => ({
          employee_id: employeeResult.id,
          insurance_type_id: insurance.id,
          is_enabled: insurance.is_enabled,
          custom_rate_percent: insurance.is_custom_rate ? insurance.rate_percent : null
        }));

        await supabase
          .from('employee_insurance_settings')
          .insert(insuranceData);
      }

      toast({
        title: 'Erfolg',
        description: employeeId ? 'Mitarbeiter aktualisiert' : 'Mitarbeiter erstellt'
      });

      onSuccess?.();
    } catch (error) {
      console.error('Error saving employee:', error);
      toast({
        title: 'Fehler',
        description: 'Fehler beim Speichern der Mitarbeiterdaten',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      <Tabs defaultValue="personal" className="space-y-6">
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="personal">Persönlich</TabsTrigger>
          <TabsTrigger value="address">Adresse</TabsTrigger>
          <TabsTrigger value="payroll">Lohnabrechnung</TabsTrigger>
          <TabsTrigger value="banking">Banking</TabsTrigger>
          <TabsTrigger value="work">Arbeit</TabsTrigger>
        </TabsList>

        <TabsContent value="personal" className="space-y-6">
          <PersonalInfoSection
            register={register}
            errors={errors}
            setValue={setValue}
            watch={watch}
          />
          
          {watchedValues.nationality && watchedValues.nationality !== 'Swiss' && (
            <WorkPermitSection
              register={register}
              errors={errors}
              setValue={setValue}
              watch={watch}
            />
          )}
        </TabsContent>

        <TabsContent value="address">
          <AddressSection
            register={register}
            errors={errors}
            setValue={setValue}
            watch={watch}
          />
        </TabsContent>

        <TabsContent value="payroll">
          <SwissPayrollSection
            employeeAge={employeeAge}
            monthlySalary={watchedValues.monthly_salary}
            hourlySalary={watchedValues.hourly_rate}
            paymentModel={watchedValues.payment_model}
            selectedCanton={watchedValues.selected_canton}
            selectedInsurances={selectedInsurances}
            vacationModel={watchedValues.vacation_model}
            annualVacationDays={watchedValues.annual_vacation_days}
            vacationPercentage={watchedValues.vacation_percentage}
            onCantonChange={(canton) => setValue('selected_canton', canton)}
            onInsurancesChange={setSelectedInsurances}
            onVacationModelChange={(model) => setValue('vacation_model', model)}
            onAnnualVacationDaysChange={(days) => setValue('annual_vacation_days', days)}
            onVacationPercentageChange={(percentage) => setValue('vacation_percentage', percentage)}
          />
        </TabsContent>

        <TabsContent value="banking">
          <div className="space-y-6">
            <BankingSection
              register={register}
              errors={errors}
            />
            
            <InsuranceSection
              register={register}
              errors={errors}
              setValue={setValue}
              watch={watch}
            />
          </div>
        </TabsContent>

        <TabsContent value="work">
          <Card>
            <CardHeader>
              <CardTitle>Anstellungsdetails</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Work details form fields would go here - keeping existing structure */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Rolle</label>
                  <select {...register('role')} className="w-full p-2 border rounded">
                    <option value="employee">Mitarbeiter</option>
                    <option value="supervisor">Supervisor</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
                
                <div className="space-y-2">
                  <label className="text-sm font-medium">Abteilung</label>
                  <input {...register('department')} className="w-full p-2 border rounded" />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Zahlungsmodell</label>
                  <select 
                    {...register('payment_model')} 
                    className="w-full p-2 border rounded"
                    onChange={(e) => setValue('payment_model', e.target.value as 'monthly' | 'hourly')}
                  >
                    <option value="monthly">Monatslohn</option>
                    <option value="hourly">Stundenlohn</option>
                  </select>
                </div>

                {watchedValues.payment_model === 'hourly' ? (
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Stundenlohn (CHF)</label>
                    <input 
                      type="number" 
                      step="0.01" 
                      {...register('hourly_rate')} 
                      className="w-full p-2 border rounded" 
                    />
                  </div>
                ) : (
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Monatslohn (CHF)</label>
                    <input 
                      type="number" 
                      {...register('monthly_salary')} 
                      className="w-full p-2 border rounded" 
                    />
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <div className="flex justify-end gap-4">
        {onCancel && (
          <Button type="button" variant="outline" onClick={onCancel} disabled={loading}>
            Abbrechen
          </Button>
        )}
        <Button type="submit" disabled={loading}>
          {loading ? 'Speichere...' : (employeeId ? 'Aktualisieren' : 'Erstellen')}
        </Button>
      </div>
    </form>
  );
};
